<template>
  <div class="flex flex-col xlr:flex-row gap-12 items-center xlr:justify-between z-10 w-full">
    <div class="flex flex-col gap-10 xlr:w-1/2 3xl:w-auto 3xl:grow">
      <div class="flex flex-col gap-3 sm:gap-7 z-10 text-center xlr:text-start">
        <h2 class="h1 lg:text-4xl">Simple Ingredients<br class="sm:hidden"> That Fuel You</h2>
        <h3 class="h2">No artificial ingredients or sweeteners,<br class="sm:hidden"> non-GMO, and<br class="hidden xlr:inline-block 3xl:hidden"> always gluten-free.</h3>
      </div>
      <div
        ref="containerRef"
        class="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-2 lg:gap-5 lg:max-xlr:w-[740px] xlr:w-[583px] 3xl:w-[740px]"
      >
        <IngredientCard
          v-for="card in cards"
          :key="card.id"
          :title="card.title"
          :model-value="expandedCard === card.id"
          @update:modelValue="toggleCard(card.id)"
        >
          <template #expanded>
            <IngredientCardSection
              v-for="section in visibleSections(card.sections)"
              :key="section.title"
              :title="section.title"
              :items="section.items"
              :src="section.src"
            />
          </template>
        </IngredientCard>
      </div>
    </div>
    <Transition
      name="ingredient-popover"
      mode="out-in"
    >
      <component
        :is="activePopover"
        v-if="activePopover"
        class="hidden! lg:flex! xlr:w-1/2 3xl:w-auto"
        :page="props.page"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type Component } from 'vue'
import IngredientCard from './ingredient-card.vue'
import IngredientCardSection from './ingredient-card-section.vue'
import PopoverCardProtein from './popover-card-protein.vue'
import PopoverCardWholegrain from './popover-card-wholegrain.vue'
import PopoverCardFunctional from './popover-card-functional.vue'
import PopoverCardRealFood from './popover-card-real-food.vue'
import { useBreakpoints } from '@/plugins/global.01-breakpoints'
import { ingredientCards, visibleSections as filterVisibleSections, type IngredientSection } from '@/utils/ingredient-cards'

export interface Props {
  page: 'home' | 'vegan'
}

const props = defineProps<Props>()

const popovers: Component[] = [ PopoverCardProtein, PopoverCardWholegrain, PopoverCardFunctional, PopoverCardRealFood ]

const cards = ingredientCards.map((card, index) => ({ ...card, popover: popovers[index] }))

const expandedCard = ref<number | undefined>(cards[0].id)
const containerRef = ref<HTMLElement | null>(null)
const activePopover = computed(() => cards.find(c => c.id === expandedCard.value)?.popover)
const breakpoints = useBreakpoints()
const isDesktop = computed(() => !breakpoints.lg.value)

const visibleSections = (sections: IngredientSection[]) => filterVisibleSections(sections, props.page)

const toggleCard = (id: number) => {
  if (expandedCard.value === id) {
    // Desktop always shows an expanded card (drives the active popover); mobile can collapse to none.
    expandedCard.value = isDesktop.value ? id : undefined
  } else {
    expandedCard.value = id
  }
}
</script>
